import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useAdminSession } from '../hooks/useAdminSession.js';
import { useTheme } from '../contexts/ThemeContext.jsx';
import { getApiUrl } from '../utils/apiClient.js';
import AdminSessionWarning from './AdminSessionWarning.jsx';

/**
 * AdminLayout component providing theme-aware layout structure for admin pages
 * Matches the user dashboard layout pattern with welcome header
 */
const AdminLayout = ({ children }) => {
  const { getToken, isSignedIn, isLoaded, signOut } = useAuth();
  const { sessionWarning, timeRemaining, extendSession, handleTimeout } = useAdminSession(30);
  const { theme, toggleTheme, loading: themeLoading } = useTheme();
  
  // Build nav link classes using Tailwind dark variants
  const getNavLinkClasses = (path) => {
    const isActive = window.location.pathname === path || (path === '/admin' && (window.location.pathname === '/admin/' || window.location.pathname === '/admin'));
    const base = 'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors';
    return isActive
      ? `${base} bg-teal-100 text-teal-800 dark:bg-teal-600 dark:text-white`
      : `${base} text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white`;
  };
  const navigate = useNavigate();
  const [userFirstName, setUserFirstName] = useState('');
  const [userDataLoaded, setUserDataLoaded] = useState(false);
  const [userDataError, setUserDataError] = useState(null);

  // Load admin user data for greeting
  useEffect(() => {
    const loadAdminData = async () => {
      if (!isSignedIn || !isLoaded) {
        setUserDataLoaded(true);
        return;
      }

      try {
        const token = await getToken();
        if (!token) {
          setUserDataError('No authentication token available');
          setUserDataLoaded(true);
          return;
        }

        const response = await fetch(getApiUrl('/api/auth/me'), {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const userData = await response.json();
          setUserFirstName(userData.user?.first_name || '');
          setUserDataError(null);
        } else {
          setUserDataError('Failed to load user data');
        }
      } catch (error) {
        console.warn('Failed to load admin user data:', error);
        setUserDataError(error.message);
      } finally {
        setUserDataLoaded(true);
      }
    };

    loadAdminData();
  }, [isSignedIn, isLoaded, getToken]);

  // Generate time-based greeting (same as user dashboard)
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    const firstName = userFirstName || 'there';

    if (hour >= 0 && hour < 12) {
      return `Good morning, ${firstName}`;
    } else if (hour >= 12 && hour < 18) {
      return `Good afternoon, ${firstName}`;
    } else {
      return `Good evening, ${firstName}`;
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out failed:', error);
      // Fallback: navigate to home page anyway
      navigate('/');
    }
  };

  // Show loading state while theme is loading
  if (themeLoading) {
    return (
      <div className={"min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900"} data-theme={theme}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className={"text-gray-600 dark:text-gray-300 dark:text-gray-600"}>
            Loading admin dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={"min-h-screen flex bg-gray-50 dark:bg-gray-900"}
      data-theme={theme}
      data-testid="admin-layout"
    >
      {/* Session timeout warning */}
      <AdminSessionWarning
        isVisible={sessionWarning}
        timeRemaining={timeRemaining}
        onExtendSession={extendSession}
        onSignOut={handleTimeout}
      />

      {/* Sidebar Navigation */}
      <aside className={"w-64 bg-white dark:bg-gray-800 shadow-lg flex flex-col"}>
        <div className="p-6 flex-1">
          <div className="flex items-center gap-2 mb-8">
            <img src="/logo.svg" alt="ParrotMate Logo" className="h-8 w-8" />
            <span className={"font-bold text-xl text-teal-600 dark:text-teal-400"}>
              ParrotMate Admin
            </span>
          </div>

          <nav className="space-y-2">
            <a href="/admin" className={getNavLinkClasses('/admin')}>
              <span className="text-xl">📊</span>
              <span>Overview</span>
            </a>

            <a href="/admin/users" className={getNavLinkClasses('/admin/users')}>
              <span className="text-xl">👥</span>
              <span>Users</span>
            </a>

            <a href="/admin/parrots" className={getNavLinkClasses('/admin/parrots')}>
              <span className="text-xl">🦜</span>
              <span>Parrots</span>
            </a>

            <a href="/admin/rag" className={getNavLinkClasses('/admin/rag')}>
              <span className="text-xl">📚</span>
              <span>Knowledge Base</span>
            </a>

            <a href="/admin/phone-numbers" className={getNavLinkClasses('/admin/phone-numbers')}>
              <span className="text-xl">📞</span>
              <span>Phone Numbers</span>
            </a>

            <a href="/admin/phone-queue" className={getNavLinkClasses('/admin/phone-queue')}>
              <span className="text-xl">⏳</span>
              <span>Phone Queue</span>
            </a>

            <a href="/admin/call-recordings" className={getNavLinkClasses('/admin/call-recordings')}>
              <span className="text-xl">🎙️</span>
              <span>Call Recordings</span>
            </a>

            <a href="/admin/audit" className={getNavLinkClasses('/admin/audit')}>
              <span className="text-xl">📋</span>
              <span>Audit Logs</span>
            </a>

            <a href="/admin/notifications" className={getNavLinkClasses('/admin/notifications')}>
              <span className="text-xl">🔔</span>
              <span>Notifications</span>
            </a>

            <a href="/admin/quick-templates" className={getNavLinkClasses('/admin/quick-templates')}>
              <span className="text-xl">📝</span>
              <span>Quick Templates</span>
            </a>

            <a href="/admin/analytics" className={getNavLinkClasses('/admin/analytics')}>
              <span className="text-xl">📊</span>
              <span>Analytics</span>
            </a>

            <a href="/admin/system-health" className={getNavLinkClasses('/admin/system-health')}>
              <span className="text-xl">🔧</span>
              <span>System Health</span>
            </a>

            <a href="/admin/settings" className={getNavLinkClasses('/admin/settings')}>
              <span className="text-xl">⚙️</span>
              <span>Settings</span>
            </a>
          </nav>
        </div>

        {/* Sign Out Button at Bottom */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleSignOut}
            className={"w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-gray-700 hover:bg-red-100 hover:text-red-800 dark:text-gray-300 dark:hover:bg-red-600 dark:hover:text-white dark:text-gray-600 dark:text-white"}
            title="Sign out of admin dashboard"
          >
            <span className="text-xl">🚪</span>
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Welcome Header */}
        <div className="px-6 pt-6 pb-2 flex justify-between items-center">
          <div>
            {userDataLoaded ? (
              userDataError ? (
                <h1 className={`text-2xl font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Good day, Administrator
                </h1>
              ) : (
                <h1 className={`text-2xl font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {getTimeBasedGreeting()}
                </h1>
              )
            ) : (
              <div className="h-8 flex items-center">
                <div
                  className={`animate-pulse h-6 rounded w-48 ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}
                  data-testid="user-greeting-skeleton"
                ></div>
              </div>
            )}
          </div>

          {/* Theme Toggle and Navigation */}
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${theme === 'dark'
                ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900'
                }`}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>

            <button
              onClick={() => navigate('/dashboard')}
              className={`px-4 py-2 rounded-lg transition-colors ${theme === 'dark'
                ? 'bg-teal-600 text-white hover:bg-teal-700'
                : 'bg-teal-600 text-white hover:bg-teal-700'
                }`}
              aria-label="Back to main dashboard"
            >
              Back to App
            </button>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="px-6 pb-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
